import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, CircularProgress, Alert, useTheme } from '@mui/material';
import { 
  fetchWorkers, 
  fetchAttendance, 
  fetchPerformanceReviews, 
  fetchPayrollRecords 
} from '../../services/workerService';
import ChartComponent from '../charts/chart';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface AnalyticsDashboardProps {
  farmId: string;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ farmId }) => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onLeave: 0,
    payroll: 0
  });
  const [chartData, setChartData] = useState<any>({
    roles: null,
    attendance: null,
    performance: null,
    payrollHistory: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [workers, attendance, reviews, payrolls] = await Promise.all([
        fetchWorkers(farmId),
        fetchAttendance(farmId),
        fetchPerformanceReviews(farmId),
        fetchPayrollRecords(farmId)
      ]);

      // Calculate Stats
      const total = workers.length;
      const active = workers.filter((w: any) => w.status === 'active').length;
      const onLeave = workers.filter((w: any) => w.status === 'on-leave').length;
      const payrollSum = payrolls.reduce((sum: number, p: any) => sum + (p.totalPay || 0), 0);
      
      setStats({ total, active, onLeave, payroll: payrollSum });

      // Role Distribution (Pie Chart)
      const roleCounts: Record<string, number> = {};
      workers.forEach((w: any) => {
        const role = w.jobTitle || w.role || 'Unassigned';
        roleCounts[role] = (roleCounts[role] || 0) + 1;
      });
      
      const rolesData = {
        labels: Object.keys(roleCounts),
        datasets: [{
          data: Object.values(roleCounts),
          backgroundColor: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.error.main,
            theme.palette.warning.main,
            theme.palette.info.main,
            theme.palette.success.main,
          ],
          borderWidth: 1
        }]
      };

      // Attendance Trends (Line Chart) - Last 7 days
      const last7Days = [...Array(7)].map((_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        return d.toISOString().split('T')[0];
      });

      const attendanceByDate: Record<string, number> = {};
      attendance.forEach((r: any) => {
        const date = r.date.split('T')[0];
        if (r.status === 'present') {
          attendanceByDate[date] = (attendanceByDate[date] || 0) + 1;
        }
      });

      const attendanceData = {
        labels: last7Days.map(d => new Date(d).toLocaleDateString(undefined, { weekday: 'short' })),
        datasets: [{
          label: 'Present Workers',
          data: last7Days.map(d => attendanceByDate[d] || 0),
          borderColor: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
          tension: 0.4,
          fill: true
        }]
      };

      // Performance Distribution (Bar Chart)
      const scores = [0, 0, 0, 0, 0]; // 1 to 5
      reviews.forEach((r: any) => {
        const score = Math.round(Number(r.score));
        if (score >= 1 && score <= 5) {
          scores[score - 1]++;
        }
      });

      const performanceData = {
        labels: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
        datasets: [{
          label: 'Performance Reviews',
          data: scores,
          backgroundColor: theme.palette.primary.light,
          borderColor: theme.palette.primary.main,
          borderWidth: 1
        }]
      };

      // Payroll Overview (Bar Chart) - Mocking monthly if not enough data, or using records
      // Group by month
      const payrollByMonth: Record<string, number> = {};
      payrolls.forEach((p: any) => {
        const month = new Date(p.periodStart).toLocaleString('default', { month: 'short' });
        payrollByMonth[month] = (payrollByMonth[month] || 0) + (p.totalPay || 0);
      });
      
      const payrollHistoryData = {
        labels: Object.keys(payrollByMonth).length ? Object.keys(payrollByMonth) : ['Current'],
        datasets: [{
          label: 'Total Payroll Cost',
          data: Object.keys(payrollByMonth).length ? Object.values(payrollByMonth) : [payrollSum],
          backgroundColor: theme.palette.info.light,
        }]
      };

      setChartData({
        roles: rolesData,
        attendance: attendanceData,
        performance: performanceData,
        payrollHistory: payrollHistoryData
      });

    } catch (err: any) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">Total Workers</Typography>
              <Typography variant="h3">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">Active</Typography>
              <Typography variant="h3" color="success.main">{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">On Leave</Typography>
              <Typography variant="h3" color="warning.main">{stats.onLeave}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">Payroll Cost</Typography>
              <Typography variant="h3" color="info.main">
                {stats.payroll.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Recorded</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Workforce Distribution</Typography>
              {chartData.roles && (
                <ChartComponent 
                  id="roles-chart"
                  type="pie" 
                  data={chartData.roles} 
                  options={{
                    plugins: { legend: { position: 'right' } }
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Attendance Trends (Last 7 Days)</Typography>
              {chartData.attendance && (
                <ChartComponent 
                  id="attendance-chart"
                  type="line" 
                  data={chartData.attendance}
                  options={{
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } }
                    }
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Ratings</Typography>
              {chartData.performance && (
                <ChartComponent 
                  id="performance-chart"
                  type="bar" 
                  data={chartData.performance}
                  options={{
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } }
                    }
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Payroll History</Typography>
              {chartData.payrollHistory && (
                <ChartComponent 
                  id="payroll-chart"
                  type="bar" 
                  data={chartData.payrollHistory}
                  options={{
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard;
